import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Button, Link, SxProps, Tab, Tabs, TextField, Theme, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Tooltip from '@mui/material/Tooltip';
import { useSnackbar } from 'notistack';

import { currentTaskFormApi } from '../../axios';
import { useFeatureFlags, useLanguage } from '../../hooks';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  addFormPublicAccess,
  getPublicLink,
  getTaskForm,
  removeFormPublicAccess,
} from '../../store/modules/currentTaskForm.slice';
import { datascopeTracker } from '../../utils/eventTracker';
import { openInNewTab } from '../../utils/navigation';
import { DSSpinner } from '../uiComponents/logoSpinner';
import { MultiEmailInput } from '../uiComponents/multiEmailInput';
import { TabPanel } from '../uiComponents/tabPanel';
import DSIcon from './../../datascope-icon.png';

// Lazy load QR code component to reduce initial bundle size
// This component uses the 'qr-code-styling' library which is ~50KB and only needed
// when users want to share forms via QR code (not always used)
const QrCodeStylingComponent = lazy(() => import('./styledQR'));

const customStyles: Record<string, SxProps<Theme>> = {
  deviceTabs: {
    borderBottom: '1px solid #e8e8e8',
    '& .MuiTabs-indicator': {
      backgroundColor: '#1890ff',
    },
  },
  deviceTab: {
    textTransform: 'none',
    fontSize: (theme: Theme) => theme.typography.h3.fontSize,
    minWidth: '72px',
    color: 'black',
    '&:hover': {
      color: '#40a9ff',
      opacity: '1px',
    },
    '&$selected': {
      color: '#1890ff',
    },
    '&:focus': {
      color: '#40a9ff',
    },
  },
  dialogTitle: {
    fontSize: (theme: Theme) => theme.typography.h1.fontSize,
    textAlign: 'left',
    marginLeft: '1%',
    color: 'black',
    width: '100%',
    marginBottom: '12px',
  },
  dialogInstruction: {
    fontSize: (theme: Theme) => theme.typography.h3.fontSize,
    textAlign: 'center',
  },
  dialogContent: {
    maxWidth: '570px',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    '@media (max-width: 1200px)': {
      width: '60vw',
    },
    '@media (max-width: 900px)': {
      width: '80vw',
    },
  },
  dialogContentText: {
    fontSize: (theme: Theme) => theme.typography.h5.fontSize,
    textAlign: 'justify',
  },
  appStore: {
    textDecoration: 'none',
    color: 'black',
    fontWeight: 'bold',
  },
  appStoreLink: {
    cursor: 'pointer',
    marginTop: '20px',
    display: 'flex',
    alignItems: 'center',
  },
  accessYourForm: {
    color: 'black',
    fontWeight: 'bolder',
    width: '100px',
    textAlign: 'center',
  },
  containedButton: {
    marginTop: '8px',
    background: '#3E8FFA',
    display: 'block',
    cursor: 'pointer',
    color: '#FFFF',
    border: `2px solid #3E8FFA`,
    flex: '1',
    borderRadius: '30px',
    textTransform: 'none',
    letterSpacing: '0.03em',
    '&:hover': {
      transform: 'translateY(-1px)',
      color: '#3E8FFA',
    },
    '@media (max-width: 600px)': {
      width: '70%',
    },
  },
  computerContainedButton: {
    background: '#3E8FFA',
    cursor: 'pointer',
    color: '#FFFF',
    border: `2px solid #3E8FFA`,
    flex: '1',
    borderRadius: '30px',
    textTransform: 'none',
    padding: '0.1em',
    marginLeft: '10px',
    '&:hover': {
      transform: 'translateY(-1px)',
      color: '#3E8FFA',
    },
    '&:disabled': {
      backgroundColor: '#e0e0e0',
      color: '#9e9e9e',
      cursor: 'not-allowed',
      borderColor: '#e0e0e0',
    },
  },
  computerWhatsappContainedButton: {
    background: '#3E8FFA',
    cursor: 'pointer',
    color: '#FFFF',
    border: `2px solid #3E8FFA`,
    borderRadius: '30px',
    textTransform: 'none',
    maxHeight: '31px',
    padding: '0.1em',
    marginLeft: '10px',
    '&:hover': {
      transform: 'translateY(-1px)',
      color: '#3E8FFA',
    },
    '&:disabled': {
      backgroundColor: '#e0e0e0',
      color: '#9e9e9e',
      cursor: 'not-allowed',
      borderColor: '#e0e0e0',
    },
  },
  QRContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  QRButtonContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  tabPanelContainer: {
    height: '100%',
    width: '100%',
  },
  tabPanelBox: {
    display: 'flex',
    alignItems: 'center',
    padding: '40px',
  },
  tabPanelTypography: {
    width: '100%',
  },
  headerShareButton: {
    borderRadius: '24px',
    paddingRight: '20px',
    paddingLeft: '20px',
  },
  dialogPaper: {
    borderRadius: '20px',
    maxHeight: '650px',
    position: 'relative',
    margin: '0px',
  },
  dialogContentInsideContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'white',
    maxHeight: '650px',
  },
  dialogContentInsideTextContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  computerPanelInputLabels: {
    fontSize: (theme: Theme) => theme.typography.h3.fontSize,
    textAlign: 'justify',
    fontWeight: 'bold',
  },
  computerPanelInputContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  textInput: {
    width: '-webkit-fill-available',
    marginTop: '4px',
    '& .MuiTextField-root': {
      marginTop: '5px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: '8px',
    },
  },
  computerPanelEmailInputButton: {
    fontSize: '13.5px',
    padding: '0.1em',
    marginLeft: '10px',
  },
  computerPanelElementContainer: {
    marginTop: '20px',
  },
  computerPanelWhatsappContainer: {
    display: 'flex',
  },
  computerPanelWhatsappTypography: {
    fontSize: (theme: Theme) => theme.typography.h4.fontSize,
    color: '#4f4f4f',
    placeSelf: 'center',
    flex: 2,
  },
  computerPanelFooterContainer: {
    display: 'flex',
    flexDirection: 'row',
    height: '3vh',
    marginLeft: '12px',
  },
  computerPanelFooterLink: {
    fontSize: (theme: Theme) => theme.typography.h4.fontSize,
    color: '#3E8FFA',
  },
  computerPanelFooterLinkMargin: {
    fontSize: (theme: Theme) => theme.typography.h4.fontSize,
    color: '#3E8FFA',
  },
  computerPanelFooterCopy: {
    color: '#3E8FFA',
    fontSize: '15px',
  },
  computerPanelFooterLock: {
    color: '#3E8FFA',
    fontSize: '15px',
    marginLeft: '15px',
  },
  closeIcon: {
    color: 'gray',
    cursor: 'pointer',
  },
  tabsHeaderContainer: {
    width: '100%',
  },
  shareViaEmailTip: {
    fontSize: (theme: Theme) => theme.typography.h5.fontSize,
    color: '#46484D',
    marginTop: '8px',
    letterSpacing: '-0.02em',
  },
};

type ModalProps = {
  isOpen: boolean;
  onClosed: () => void;
};

const WHATSAPP_API_HOST = 'https://api.whatsapp.com';

const generatePublicWebformUrl = (params: {
  language?: 'en' | 'es' | 'pt';
  share_token?: string;
  task_form_id?: string;
  task_assign_id?: string;
  finding?: string;
}) => {
  const searchParams = new URLSearchParams();

  if (params.language) searchParams.set('language', params.language);
  if (params.share_token) searchParams.set('share_token', params.share_token);
  if (params.task_form_id) searchParams.set('task_form_id', params.task_form_id);
  if (params.task_assign_id) searchParams.set('task_assign_id', params.task_assign_id);
  if (params.finding) searchParams.set('finding', params.finding);

  const queryString = searchParams.toString();
  return `/public/webforms${queryString ? `?${queryString}` : ''}`;
};

const ShareModal = ({ isOpen, onClosed }: ModalProps) => {
  const lang = useLanguage();
  const [copied, setCopied] = useState(false);
  const [shareEmails, setShareEmails] = useState<string[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  const { checkFlag } = useFeatureFlags();
  const onboarding = useMemo(() => checkFlag('OnboardingQR'), [checkFlag]);

  // Redux functions and hooks
  const { currentTaskFormId, currentTaskForm, publicLink, isActive } = useAppSelector(
    (state) => state.currentTaskForm,
  );
  const dispatch = useAppDispatch();

  // EFFECTS:
  useEffect(() => {
    const fetchShareModalData = async () => {
      if (isOpen && currentTaskFormId) {
        await dispatch(getTaskForm({ formId: currentTaskFormId }));
        await dispatch(getPublicLink({ formId: currentTaskFormId }));
      } else {
        // reset the modal tab and clean the email input:
        setTab(0);
        setShareEmails([]);
      }
    };
    fetchShareModalData();
  }, [isOpen, currentTaskFormId]);

  // MEMO VARIABLES:
  const token = useMemo(() => currentTaskForm?.link_token, [currentTaskForm]);
  const data = useMemo(() => {
    const shareLink = `https://app.datascope.io/?link=https%3A%2F%2Fmydatascope.com%2Fresume%3Flogin_token%3D
      ${token}
      %26form_id%3D
      ${currentTaskFormId}
      &login_token=
      ${token}
      &apn=com.datascope&isi=1132697668&ibi=org.datascope.form&utm_source=email_invitation&utm_medium=profile_setup`;
    return shareLink;
  }, [currentTaskFormId]);

  const displayPublicLink = () => {
    if (!publicLink || !currentTaskFormId) return undefined;

    try {
      const url = new URL(publicLink);
      const shareToken = url.searchParams.get('share_token');

      if (shareToken) {
        const path = generatePublicWebformUrl({
          share_token: shareToken,
          task_form_id: currentTaskFormId.toString(),
        });
        return `${window.location.origin}${path}`;
      }
    } catch (error) {
      console.error('Error parsing publicLink URL:', error);
    }

    return publicLink;
  };

  const onClickPublicAccess = useCallback(
    async (publicAccess: 'ADD' | 'REMOVE') => {
      const formId = currentTaskFormId;
      if (publicAccess === 'ADD') {
        await dispatch(addFormPublicAccess({ formId }));
        await dispatch(getPublicLink({ formId }));
      }
      if (publicAccess === 'REMOVE') await dispatch(removeFormPublicAccess({ formId }));

      // feedback:
      if (publicAccess === 'ADD') {
        enqueueSnackbar(lang.form_builder.formMadePublic, {
          anchorOrigin: { horizontal: 'center', vertical: 'top' },
          variant: 'success',
        });
        datascopeTracker('Make form public', { source: 'FORM_BUILDER', formId }, 'AMP');
      } else {
        enqueueSnackbar(lang.form_builder.undoFormPublic, {
          anchorOrigin: { horizontal: 'center', vertical: 'top' },
          variant: 'success',
        });
        datascopeTracker('Remove form public access', { source: 'FORM_BUILDER', formId }, 'AMP');
      }
    },
    [currentTaskFormId],
  );

  const onboardingCheck = () => {
    const downloadApp = () => openInNewTab(lang.links.appLink, true);

    if (onboarding && data) {
      return (
        <>
          <Box sx={customStyles.QRContainer}>
            <Suspense fallback={<DSSpinner />}>
              <QrCodeStylingComponent data={data}></QrCodeStylingComponent>
            </Suspense>
          </Box>
          <DialogContentText sx={customStyles.dialogInstruction}>
            {lang.form_builder.testYourForm4}
          </DialogContentText>
          <Box sx={customStyles.QRButtonContainer}>
            <Button
              onClick={downloadApp}
              sx={customStyles.containedButton}
            >
              {lang.form_builder.downloadApp}
            </Button>
          </Box>
        </>
      );
    } else {
      return (
        <>
          <DialogContentText sx={customStyles.dialogContentText}>
            {lang.form_builder.testYourForm1}
            <Link
              sx={customStyles.appStore}
              href={lang.links.appStore}
              rel={'noreferrer'}
              target={'_blank'}
              underline='hover'
            >
              App Store
            </Link>
            {' & '}
            <Link
              sx={customStyles.appStore}
              href={lang.links.playStore}
              rel={'noreferrer'}
              target={'_blank'}
              underline='hover'
            >
              Play Store
            </Link>
            . {lang.form_builder.testYourForm2}
          </DialogContentText>
          <Box
            sx={customStyles.appStoreLink}
            onClick={() => openInNewTab(lang.links.appLink)}
          >
            <img
              alt={'DSIcon'}
              src={DSIcon}
              height={50}
            />
            <Typography
              sx={customStyles.accessYourForm}
              variant='h5'
            >
              {lang.form_builder.accessYourForm}
            </Typography>
          </Box>
        </>
      );
    }
  };

  const [tab, setTab] = React.useState(0);

  const handleSwitchTab = (
    event: React.ChangeEvent<object>,
    newTab: React.SetStateAction<number>,
  ) => {
    setTab(newTab);
  };

  const handleShareViaEmail = async () => {
    const joinedEmailsToSend = shareEmails.join(',');
    try {
      const response = await currentTaskFormApi.shareTaskFormByEmail(
        currentTaskFormId,
        joinedEmailsToSend,
      );
      enqueueSnackbar(`${response.invitations_sent} ${lang.form_builder.snackBarShared}`, {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(`No ${lang.form_builder.snackBarShared}`, {
        variant: 'error',
      });
    }
  };

  function handleShareViaWhatsapp() {
    const formName = currentTaskForm?.name ?? '';
    const message = lang.form_builder.shareWhatsappMessageApi.replace('{form_name}', formName);
    const text = `${message}${displayPublicLink()}`;
    openInNewTab(
      `${WHATSAPP_API_HOST}/send?text=${encodeURIComponent(text)}`,
      true,
    );
  }

  return (
    <Dialog
      open={isOpen}
      scroll={'body'}
      onClose={onClosed}
      maxWidth={'md'}
      PaperProps={{ sx: customStyles.dialogPaper }}
    >
      <DialogContent sx={customStyles.dialogContent}>
        <Box sx={customStyles.dialogContentInsideContainer}>
          <Box sx={customStyles.dialogContentInsideTextContainer}>
            <DialogContentText sx={customStyles.dialogTitle}>
              {lang.form_builder.shareForm2}
            </DialogContentText>
            <CloseIcon
              sx={customStyles.closeIcon}
              onClick={onClosed}
            />
          </Box>
          <Box sx={customStyles.tabsHeaderContainer}>
            <Tabs
              value={tab}
              sx={customStyles.deviceTabs}
              onChange={handleSwitchTab}
              indicatorColor='primary'
              textColor='inherit'
            >
              <Tab
                label={lang.form_builder.mobileTab}
                sx={customStyles.deviceTab}
              />
              <Tab
                label={lang.form_builder.webTab}
                sx={customStyles.deviceTab}
              />
            </Tabs>
          </Box>
          <TabPanel
            value={tab}
            index={0}
          >
            {onboardingCheck()}
          </TabPanel>
          <TabPanel
            value={tab}
            index={1}
          >
            <Box>
              <Box>
                <DialogContentText sx={customStyles.computerPanelInputLabels}>
                  {lang.form_builder.shareForm}
                </DialogContentText>
                <Box>
                  <Box sx={customStyles.computerPanelInputContainer}>
                    <TextField
                      fullWidth
                      size='small'
                      variant='outlined'
                      value={displayPublicLink() || ''}
                      sx={customStyles.textInput}
                      disabled
                    />
                    <Button
                      onClick={() => openInNewTab(displayPublicLink(), true)}
                      sx={customStyles.computerContainedButton}
                      disabled={!publicLink}
                    >
                      {lang.form_builder.visitForm}
                    </Button>
                  </Box>
                </Box>
              </Box>
              <Box sx={customStyles.computerPanelElementContainer}>
                <DialogContentText sx={customStyles.computerPanelInputLabels}>
                  {lang.form_builder.shareEmail}
                </DialogContentText>
                <Box>
                  <Box sx={customStyles.computerPanelInputContainer}>
                    <MultiEmailInput
                      key={currentTaskFormId}
                      emails={shareEmails}
                      onEmailChange={setShareEmails}
                      disabled={!publicLink}
                      hideLabel
                    />
                    <Button
                      onClick={handleShareViaEmail}
                      sx={customStyles.computerContainedButton}
                      disabled={shareEmails.length === 0}
                    >
                      {lang.form_answer.submit_button}
                    </Button>
                  </Box>
                </Box>
              </Box>
              <Box sx={customStyles.computerPanelElementContainer}>
                <DialogContentText sx={customStyles.computerPanelInputLabels}>
                  {lang.form_builder.shareWhatsapp}
                </DialogContentText>
                <Box sx={customStyles.computerPanelWhatsappContainer}>
                  <Typography sx={customStyles.computerPanelWhatsappTypography}>
                    {' '}
                    {lang.form_builder.shareWhatsappMessage}{' '}
                  </Typography>
                  <Button
                    onClick={handleShareViaWhatsapp}
                    sx={customStyles.computerWhatsappContainedButton}
                    variant='outlined'
                    disabled={!publicLink}
                  >
                    {lang.form_answer.submit_button}
                  </Button>
                </Box>
              </Box>
            </Box>
          </TabPanel>
        </Box>
        {tab === 1 && (
          <Box sx={customStyles.computerPanelFooterContainer}>
            {publicLink && (
              <Tooltip title={copied ? lang.form_builder.copiedLink : lang.form_builder.copyLink}>
                <Link
                  component='button'
                  underline='hover'
                  variant='body2'
                  onClick={() => {
                    navigator.clipboard.writeText(displayPublicLink());
                    setCopied(true);
                  }}
                  onMouseLeave={() => setCopied(false)}
                  sx={customStyles.computerPanelFooterLink}
                >
                  <FileCopyOutlinedIcon sx={customStyles.computerPanelFooterCopy} />
                  {lang.form_builder.copyLink}
                </Link>
              </Tooltip>
            )}

            {isActive ? (
              <Link
                component='button'
                variant='body2'
                underline='hover'
                onClick={() => onClickPublicAccess('REMOVE')}
                sx={customStyles.computerPanelFooterLink}
              >
                <LockOutlinedIcon sx={customStyles.computerPanelFooterLock} />
                {lang.form_builder.shareFormOff}
              </Link>
            ) : (
              <Link
                component='button'
                variant='body2'
                underline='hover'
                onClick={() => onClickPublicAccess('ADD')}
                sx={customStyles.computerPanelFooterLink}
              >
                <LockOpenOutlinedIcon sx={customStyles.computerPanelFooterLock} />
                {lang.form_builder.shareFormOn}
              </Link>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { ShareModal };
